import { useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import AddFile from "../../assets/addFile.svg";
import * as XLSX from "xlsx";

function FileInput({ onSelectDocument }) {
    const { t } = useTranslation();
    const [progressMessage, setProgressMessage] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const city = "Región Metropolitana";

    // Función para obtener el país desde el número de teléfono
    const getCountryFromPhone = (phone) => {
        const phoneStr = String(phone).trim();
        
        // Chile: +56, 56, o empieza con 9 (sin código de país)
        if (phoneStr.startsWith("+56") || phoneStr.startsWith("56") || /^9\d{8}$/.test(phoneStr)) {
            return "Chile";
        }
        // Argentina: +54 o 54
        if (phoneStr.startsWith("+54") || phoneStr.startsWith("54")) {
            return "Argentina";
        }
        // Brasil: +55 o 55
        if (phoneStr.startsWith("+55") || phoneStr.startsWith("55")) {
            return "Brasil";
        }
        // Estados Unidos: +1 o 1
        if (phoneStr.startsWith("+1") || phoneStr.startsWith("1")) {
            return "Estados Unidos";
        }
        
        return null;
    };

    // Función para validar número de teléfono según los requisitos
    const isValidPhoneNumber = (phone) => {
        if (!phone) return false;
        
        const phoneStr = String(phone).trim().replace(/\s/g, "");
        
        // Chile: +56 seguido de 8-11 dígitos (puede incluir código de área)
        // Formatos válidos: +56912345678, +56212345678, +5656978778175, etc.
        // También acepta 569, 562, o empieza con 9 (8 dígitos después del 9)
        if (phoneStr.startsWith("+56")) {
            // +56 seguido de 8-11 dígitos adicionales (permite códigos de área como +562, +569)
            const digitsAfter56 = phoneStr.substring(3);
            if (/^\d{8,11}$/.test(digitsAfter56)) {
                return true;
            }
        }
        if (phoneStr.startsWith("56") && phoneStr.length >= 10) {
            // 56 seguido de 8-11 dígitos adicionales (sin el +)
            // Acepta 562, 569, etc.
            const digitsAfter56 = phoneStr.substring(2);
            if (/^\d{8,11}$/.test(digitsAfter56)) {
                return true;
            }
        }
        // Número que empieza con 9 seguido de 8 dígitos (formato local chileno)
        if (/^9\d{8}$/.test(phoneStr)) {
            return true;
        }
        
        // Argentina: +54 seguido de números
        if (phoneStr.startsWith("+54") || phoneStr.startsWith("54")) {
            return /^(\+?54)\d{8,10}$/.test(phoneStr);
        }
        
        // Brasil: +55 seguido de números
        if (phoneStr.startsWith("+55") || phoneStr.startsWith("55")) {
            return /^(\+?55)\d{10,11}$/.test(phoneStr);
        }
        
        // Estados Unidos: +1 seguido de números
        if (phoneStr.startsWith("+1") || phoneStr.startsWith("1")) {
            return /^(\+?1)\d{10}$/.test(phoneStr);
        }
        
        return false;
    };

    const mapRowToPackage = (row, rowIndex) => {
        const country = getCountryFromPhone(String(row[3]));
        const packageType = "EXCEL";

        // Comentario del envío (campo opcional, columna 7 según especificación)
        const getComments = (row) => {
            // row[7] es el comentario del envío
            return row[7] || null;
        };

        // Extraer dirección y departamento
        const addressLine = String(row[4] || "").trim();
        const streetParts = addressLine.split(",");
        const streetName = streetParts[0] || "";
        const streetNumber = streetParts[1] ? streetParts[1].trim() : "";
        
        // Dpto es opcional (columna 5 según especificación)
        const dpto = row[5] ? String(row[5]).trim() : "";

        const packageData = {
            type: packageType,
            receiver_name: String(row[2] || "").trim(),
            receiver_lastName: "",
            receiver_phone: String(row[3] || "").trim(),
            comments: getComments(row),
            packageDimensions: {
                height: row[8] ? parseFloat(row[8]) : null,
                width: row[9] ? parseFloat(row[9]) : null,
                length: row[10] ? parseFloat(row[10]) : null,
                weight: row[11] ? parseFloat(row[11]) : null,
            },
            shipping_address: {
                street_name: streetName,
                street_number: streetNumber,
                comment: dpto ? `Dpto: ${dpto}` : null,
                lat: null, // Las coordenadas serán obtenidas por el backend
                lng: null, // Las coordenadas serán obtenidas por el backend
                city: {
                    name: city,
                },
                municipality: {
                    name: String(row[6] || "").trim(),
                },
                country: {
                    name: country || "Chile",
                },
                delivery_preference: String(row[0] || "").toLowerCase(),
                address_line: addressLine,
            },
            status: 0,
            additionalLabels: row[12] ? parseInt(row[12]) : null,
        };

        // Número de venta (campo opcional, columna 1)
        if (row[1]) {
            packageData.order_id = String(row[1]).trim();
        }

        return packageData;
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setErrorMessage("");
        setProgressMessage(t("Estamos cargando tu archivo..."));
        const reader = new FileReader();

        reader.onload = async (event) => {
            const data = new Uint8Array(event.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1 });

            let validRows = [];
            let hasError = false;
            const validationErrors = [];

            setProgressMessage(t("Vamos a verificar los campos del archivo..."));

            for (let i = 2; i < rows.length; i++) {
                const row = rows[i];

                // Si la primera columna está vacía, asumimos que no hay más filas
                if (!row[0]) break;

                const rowNumber = i + 1;
                const rowErrors = [];

                // Validar Tipo de encomienda (obligatorio, case-insensitive)
                setProgressMessage(t("Validando fila {{rowNumber}}: Tipo de encomienda...", { rowNumber }));
                const tipoEncomienda = String(row[0] || "").trim().toLowerCase();
                if (!tipoEncomienda || !["sameday", "nextday"].includes(tipoEncomienda)) {
                    rowErrors.push(t('Tipo de encomienda debe ser "sameday" o "nextday" (sin importar mayúsculas/minúsculas). Valor encontrado: "{{value}}"', { value: row[0] || "(vacío)" }));
                }

                // Número de venta es opcional, no se valida

                // Validar Nombre del destinatario (obligatorio)
                setProgressMessage(t("Validando fila {{rowNumber}}: Nombre del destinatario...", { rowNumber }));
                if (!row[2] || !String(row[2]).trim()) {
                    rowErrors.push(t("Nombre del destinatario es obligatorio y no puede estar vacío"));
                }

                // Validar Teléfono cliente (obligatorio)
                setProgressMessage(t("Validando fila {{rowNumber}}: Teléfono cliente...", { rowNumber }));
                if (!row[3] || !String(row[3]).trim()) {
                    rowErrors.push(t("Teléfono cliente es obligatorio y no puede estar vacío"));
                } else if (!isValidPhoneNumber(row[3])) {
                    const phoneStr = String(row[3]).trim();
                    rowErrors.push(
                        t('Teléfono cliente inválido. Debe ser un número válido de Chile (+56 seguido de 8-11 dígitos, +562, +569, 562, 569, o 9 seguido de 8 dígitos), ' +
                        'Argentina (+54), Brasil (+55) o Estados Unidos (+1). Valor encontrado: "{{value}}"', { value: phoneStr })
                    );
                }

                // Validar Dirección (obligatorio)
                setProgressMessage(t("Validando fila {{rowNumber}}: Dirección...", { rowNumber }));
                if (!row[4] || !String(row[4]).trim()) {
                    rowErrors.push(t("Dirección es obligatoria y no puede estar vacía"));
                }

                // Dpto es opcional, no se valida

                // Validar Comuna (obligatorio)
                setProgressMessage(t("Validando fila {{rowNumber}}: Comuna...", { rowNumber }));
                if (!row[6] || !String(row[6]).trim()) {
                    rowErrors.push(t("Comuna es obligatoria y no puede estar vacía"));
                }

                // Comentario del envío es opcional, no se valida
                // Alto, Ancho, Largo, Peso son opcionales, no se validan
                // Cantidad de bultos es opcional, no se valida

                if (rowErrors.length > 0) {
                    validationErrors.push({
                        row: rowNumber,
                        errors: rowErrors
                    });
                    hasError = true;
                    // Continuar validando otras filas para mostrar todos los errores
                    continue;
                }

                validRows.push(row);
            }

            // Si hay errores de validación, mostrar mensaje claro
            if (hasError && validationErrors.length > 0) {
                const errorMessages = validationErrors.map(err => {
                    const errorsList = err.errors.map(e => `  • ${e}`).join('\n');
                    return t("Fila {{row}}:\n{{errors}}", { row: err.row, errors: errorsList });
                }).join('\n\n');

                setErrorMessage(
                    t("Se encontraron errores en {{count}} fila(s):\n\n{{errors}}\n\n", { count: validationErrors.length, errors: errorMessages }) +
                    t("Por favor, corrija los errores y vuelva a cargar el archivo.")
                );
                setProgressMessage("");
            }

            if (!hasError && validRows.length > 0) {
                setProgressMessage(t("Validaciones completadas. Generando petición para crear las encomiendas..."));
                const packagesJSON = validRows.map((row, index) =>
                    mapRowToPackage(row, index + 2)
                );
                onSelectDocument({ file, packagesJSON });
                setProgressMessage(t("Archivo procesado con éxito. {{count}} envío(s) válido(s) listo(s) para crear.", { count: validRows.length }));
            } else if (!hasError) {
                setErrorMessage(t("No se encontraron registros válidos a partir de la fila 3. Por favor, verifique que el archivo tenga datos en las filas correspondientes."));
                setProgressMessage("");
            }
        };

        reader.readAsArrayBuffer(file);
    };

    return (
        <div className="w-3/4 mx-auto border rounded-lg px-4 py-2 flex flex-col">
            <input
                type="file"
                id="fileInput"
                className="opacity-0 w-[60%] h-[43%] cursor-pointer absolute"
                onChange={handleFileChange}
                accept=".xlsx, .xls, .csv"
            />
            <div className="d-flex justify-content-center">
                <img src={AddFile} alt="addFile" />
            </div>
            <span className="text-center mt-3 custom-upload-file-text">
                {t("Pincha aquí para seleccionar un archivo")}
            </span>
            <span className="text-center mt-3 mb-4 custom-upload-file-sub-text">
                {t("O arrástralo aquí")}
            </span>
            {progressMessage && (
                <span className="text-center text-blue-500 mt-2">{progressMessage}</span>
            )}
            {errorMessage && (
                <div className="text-center text-red-500 mt-2 flex items-center justify-center gap-2 relative">
                    <span>{errorMessage}</span>
                    <button
                        type="button"
                        onClick={() => {
                            setErrorMessage("");
                            const fileInput = document.getElementById("fileInput");
                            if (fileInput) {
                                fileInput.value = "";
                            }
                        }}
                        className="ml-2 p-1 hover:bg-red-100 rounded-full transition-colors"
                        aria-label={t("Cerrar mensaje de error y volver a cargar archivo")}
                        title={t("Cerrar y volver a cargar")}
                    >
                        <X size={16} />
                    </button>
                </div>
            )}
        </div>
    );
}

export default FileInput;